<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute, RouterView } from "vue-router";
import AppNavigation from "@/components/layout/AppNavigation.vue";
import AppHeader from "@/components/layout/AppHeader.vue";
import MainContent from "@/components/layout/MainContent.vue";
import { useWeatherStore } from "@/stores/weather.store";
import { useLocationStore } from "@/stores/location.store";
import { useLocationSearchStore } from "@/stores/locationSearch.store";
import { useWeatherSessionStore } from "@/stores/weatherSession.store";
import { useSettingsStore } from "@/stores/settings.store";
import { useDisplayStore } from "@/stores/display.store";
import { useEnsembleStore } from "@/stores/ensemble.store";
import { useSelectionBorder } from "@/composables/useSelectionBorder";

const route = useRoute();
const settings = useSettingsStore();
const display = useDisplayStore();
const wallMode = computed(() => route.name === "home" && display.wallMode);
const compactNavigation = computed(() => display.tabletMode || wallMode.value);
const session = useWeatherSessionStore();
const ensembles = useEnsembleStore();
const locations = useLocationStore();
const weather = useWeatherStore();
const locationSearch = useLocationSearchStore();

// Check if current route is the full-screen map
const isMapRoute = computed(() => route.name === "map");

/** Refresh the data owned by the currently visible page. */
function refreshActive() {
  if (route.name === "ensembles") void ensembles.refresh(true);
  else void session.refresh(true);
}

watch(
  () => route.name,
  (name) => ensembles.setActive(name === "ensembles"),
  { immediate: true },
);

useSelectionBorder();

onMounted(() => {
  display.start();
  ensembles.start();
  void session.start();
});

onUnmounted(() => {
  session.stop();
  ensembles.stop();
  display.stop();
});
</script>

<template>
  <div
    class="weather-app relative flex h-dvh flex-col overflow-hidden bg-app-bg text-text-primary"
    :class="
      compactNavigation
        ? 'p-0 [--wall-header-height:56px]'
        : 'pb-[calc(78px+env(safe-area-inset-bottom))] md:pb-0 md:pl-[104px]'
    "
  >
    <AppNavigation v-if="!compactNavigation" />
    <AppHeader
      :wall-mode="compactNavigation"
      :can-fullscreen="display.fitsWall"
      :fullscreen="display.fullscreen"
      @toggle-fullscreen="display.toggleFullscreen"
      :locations="locations.headerData"
      :summaries="weather.locationSummaries"
      :summaries-loading="weather.summariesLoading"
      :unit="settings.temperatureUnit"
      :search-state="locationSearch.state"
      :refreshing="
        route.name === 'ensembles'
          ? ensembles.loading
          : weather.isLoading || weather.isRefreshing
      "
      @refresh="refreshActive"
      @select="locations.selectLocation"
      @locate="locations.locateAndSelect"
      @save="locations.saveAndSelect"
      @remove="locations.removeSavedLocation"
      @rename="locations.renameSavedLocation"
      @query="locationSearch.setQuery"
      @choose-search="locations.viewLocation"
      @clear-search="locationSearch.clear"
    >
      <template #navigation
        ><AppNavigation v-if="compactNavigation" compact
      /></template>
    </AppHeader>

    <!-- Main Content Area -->
    <MainContent v-if="!isMapRoute" :wall-mode="wallMode" />

    <!-- Full-screen map view -->
    <div
      v-if="isMapRoute"
      class="relative isolate z-10 mx-3 mb-3 min-h-0 flex-1 overflow-hidden rounded-panel bg-app-conditions md:mx-6 md:mb-5 md:rounded-shell"
    >
      <RouterView />
    </div>
  </div>
</template>
